import * as React from 'react';

import {
    CalendarAllDaySurfaceItem,
    CalendarSurfaceBlankItem,
    CalendarSurfaceItem,
    CalendarSurfaceStore,
    getBaseTimeZone,
} from 'owa-calendar-surface-dategrid';
import { CalendarMonthOverflowIndicator, CalendarMonthSurfaceItem } from 'owa-calendar-surface';

import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { getCalendarEventColorScheme } from 'owa-calendar-color-resolver';
import { getCharm } from 'owa-calendar-events-store';
import { isCalendarPeekShown } from 'owa-calendar-module-command';
import { isSameDay } from 'owa-datetime';
import { shouldShowWeekMonthName } from 'owa-calendar-layout';

/**
 * Default in-day item template
 * @param store: A surface store
 * @param model: A surface item model
 * @param state: A surface state
 */
export let inDayItemTemplate = (
    store: CalendarSurfaceStore,
    model: CalendarEvent,
    state?: any
): JSX.Element => {
    return (
        <CalendarSurfaceItem
            key={model.ItemId.Id}
            item={model}
            colorScheme={getCalendarEventColorScheme(model)}
            charmId={getCharm(model)}
            disableTabIndex={store.disableTabbableSurfaceItems}
            itemState={{
                continuesLeft: false,
                continuesRight: false,
                timeZoneId: getBaseTimeZone(store.timeZoneIds),
            }}
            joinButtonVisible={true}
            shouldHideTooltip={isCalendarPeekShown}
        />
    );
};

/**
 * Default all-day item template
 * @param store: A surface store
 * @param model: A surface item model
 * @param state: A surface state
 */
export let allDayItemTemplate = (
    store: CalendarSurfaceStore,
    model: CalendarEvent,
    itemState?: any
): JSX.Element => {
    return (
        <CalendarAllDaySurfaceItem
            key={model.ItemId.Id}
            item={model}
            colorScheme={getCalendarEventColorScheme(model)}
            charmId={getCharm(model)}
            disableTabIndex={store.disableTabbableSurfaceItems}
            itemState={itemState}
            joinButtonVisible={true}
            shouldHideTooltip={isCalendarPeekShown}
        />
    );
};

/**
 * Default item template for month view
 * @param store: A surface store
 * @param model: A surface item model
 * @param state: A surface state
 */
export let monthItemTemplate = (
    store: CalendarSurfaceStore,
    model: CalendarEvent,
    itemState?: any
): JSX.Element => {
    return (
        <CalendarMonthSurfaceItem
            key={model.ItemId.Id}
            item={model}
            colorScheme={getCalendarEventColorScheme(model)}
            charmId={getCharm(model)}
            itemState={{
                week: itemState.week,
            }}
            userOptions={store.userOptions}
            dateRange={store.dateRange}
            joinButtonVisible={true}
            shouldHideTooltip={isCalendarPeekShown}
            shouldShowEventStartTime={shouldShowWeekMonthName()}
            isSelectedDay={isSameDay(model.Start, store.selectedDate)}
        />
    );
};

/**
 * Default blank item template
 * @param store: A surface store
 * @param model: A surface item model
 * @param state: A surface state
 */
export let blankItemTemplate = (
    store: CalendarSurfaceStore,
    model: CalendarEvent,
    itemState?: any
): JSX.Element => {
    // blank items have calPrimary overwritten by calLighter
    let colorScheme = getCalendarEventColorScheme(model);
    colorScheme = { ...colorScheme, ...{ calPrimary: colorScheme.calLighter } };

    return (
        <CalendarSurfaceBlankItem
            key={model.ItemId.Id}
            freeBusyType={model.FreeBusyType}
            colorScheme={colorScheme}
            disableTabIndex={store.disableTabbableSurfaceItems}
            hideFreeBusyBar={false}
            joinButtonVisible={false}
        />
    );
};

/**
 * Template for overflow month item
 */
export let overflowMonthItemTemplate = (
    store: CalendarSurfaceStore,
    model: CalendarEvent
): JSX.Element => {
    return (
        <CalendarMonthOverflowIndicator
            key={model.ItemId.Id}
            item={model}
            isSelectedDay={isSameDay(model.Start, store.selectedDate)}
        />
    );
};
